import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { BrandLogo } from "@/components/common/BrandLogo";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Link2Off, Clock, ShieldAlert, Phone, Mail, Info, CheckCircle2, AlertTriangle, GitBranch } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Full-page skeleton mirroring the cover + facts layout so the page does not jump when data lands. */
export const ProposalSkeleton = () => (
  <div className="min-h-screen proposal-paper" data-testid="proposal-skeleton">
    <div className="h-16 border-b border-border/60 bg-background/80">
      <div className="mx-auto flex h-full max-w-[1180px] items-center justify-between px-5 sm:px-8">
        <Skeleton className="h-7 w-44" />
        <Skeleton className="h-9 w-40" />
      </div>
    </div>
    <div className="proposal-cover">
      <div className="mx-auto grid max-w-[1180px] gap-10 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:py-28">
        <div className="space-y-5 lg:col-span-7">
          <Skeleton className="h-4 w-56 bg-white/10" />
          <Skeleton className="h-14 w-full bg-white/10" />
          <Skeleton className="h-14 w-3/4 bg-white/10" />
          <Skeleton className="h-5 w-2/3 bg-white/10" />
        </div>
        <Skeleton className="h-72 rounded-[28px] bg-white/10 lg:col-span-5" />
      </div>
    </div>
    <div className="mx-auto -mt-12 max-w-[1180px] px-5 sm:px-8">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-border/60 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="space-y-3 bg-card p-6"><Skeleton className="h-3 w-20" /><Skeleton className="h-8 w-28" /></div>
        ))}
      </div>
    </div>
  </div>
);

const ICONS = { 404: Link2Off, 410: Clock };
const TITLES = { 404: "Fant ikke tilbudet", 410: "Lenken er ikke lenger aktiv" };

/** Invalid / expired / revoked link – premium, calm, with a way to reach the company. */
export const ProposalUnavailable = ({ status, message }) => {
  const cfg = useQuery({ queryKey: ["public-config"], queryFn: () => get("/public/config"), staleTime: 5 * 60_000 });
  const Icon = ICONS[status] || ShieldAlert;
  return (
    <div className="min-h-screen proposal-paper flex flex-col" data-testid="proposal-unavailable">
      <header className="h-16 border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-full max-w-[1180px] items-center px-5 sm:px-8"><BrandLogo subtitle="Tilbud" /></div>
      </header>
      <main className="flex flex-1 items-center justify-center px-5 py-16">
        <div className="w-full max-w-lg rounded-3xl bg-card p-8 proposal-shadow ring-1 ring-border/60 sm:p-10">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted text-foreground/70"><Icon className="h-5 w-5" /></div>
          <h1 className="mt-6 text-[26px] font-semibold tracking-tight" data-testid="proposal-unavailable-title">{TITLES[status] || "Kunne ikke åpne tilbudet"}</h1>
          <p className="mt-3 text-[15px] leading-7 text-muted-foreground" data-testid="proposal-unavailable-message">{message || "Lenken er ugyldig eller har utløpt. Ta kontakt med din rådgiver for en ny lenke."}</p>
          <div className="mt-8 border-t border-border/70 pt-6">
            <div className="text-[12.5px] text-muted-foreground">Kontakt {cfg.data?.company_name || "Stord Innkvartering"}</div>
            <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2 text-[14px]">
              {cfg.data?.phone && <a href={`tel:${cfg.data.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 link" data-testid="proposal-unavailable-phone"><Phone className="h-4 w-4 text-muted-foreground" />{cfg.data.phone}</a>}
              {cfg.data?.email && <a href={`mailto:${cfg.data.email}`} className="inline-flex items-center gap-2 link" data-testid="proposal-unavailable-email"><Mail className="h-4 w-4 text-muted-foreground" />{cfg.data.email}</a>}
            </div>
          </div>
          <Button asChild variant="outline" className="mt-6 h-10 px-4"><Link to="/henvendelse" data-testid="proposal-unavailable-enquiry-link">Send ny henvendelse</Link></Button>
        </div>
      </main>
    </div>
  );
};

const BANNER = {
  accepted: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-900 ring-emerald-200/70 dark:bg-emerald-950/40 dark:text-emerald-100 dark:ring-emerald-900" },
  preview: { icon: Info, cls: "bg-amber-50 text-amber-950 ring-amber-200/70 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-900" },
  lapsed: { icon: AlertTriangle, cls: "bg-amber-50 text-amber-950 ring-amber-200/70 dark:bg-amber-950/40 dark:text-amber-100 dark:ring-amber-900" },
  superseded: { icon: GitBranch, cls: "bg-muted text-foreground ring-border" },
  declined: { icon: Info, cls: "bg-muted text-foreground ring-border" },
};

/** Slim contextual banner for every non-open state. Returns null for `open`. */
export const StateBanner = ({ data }) => {
  const s = data.state;
  if (s === "open") return null;
  const q = data.quote;
  const acc = data.response?.accepted;
  const text = {
    accepted: acc?.name ? `Tilbudet ble akseptert av ${acc.name}${acc.title ? `, ${acc.title}` : ""} ${fmtDate(acc.created_at)}. ${data.seller?.name || "Din kontaktperson"} tar kontakt for å avtale oppstart.` : `Tilbudet ble akseptert ${fmtDate(q.accepted_at)}.`,
    preview: "Dette er en forhåndsvisning av et utkast. Tilbudet er ikke sendt, og kan ikke aksepteres herfra.",
    lapsed: `Tilbudets gyldighet utløp ${fmtDate(q.valid_until)}. Du kan fortsatt be om et oppdatert tilbud nedenfor.`,
    superseded: `Denne lenken viser versjon ${q.version}. Det finnes en nyere versjon (${q.current_version}) – be din kontaktperson om oppdatert lenke.`,
    declined: "Tilbudet er markert som avsluttet. Kontakt din rådgiver dersom dette ikke stemmer.",
  }[s];
  const m = BANNER[s] || BANNER.declined;
  const Icon = m.icon;
  return (
    <div className="mx-auto max-w-[1180px] px-5 sm:px-8">
      <div className={cn("mt-8 flex items-start gap-3 rounded-2xl px-5 py-4 text-[14px] leading-6 ring-1", m.cls)} role="status" data-testid={`proposal-state-banner-${s}`}>
        <Icon className="mt-1 h-4 w-4 shrink-0" />
        <p>{text}</p>
      </div>
    </div>
  );
};
